<template>
  <section
    id="begin-with-what-you-want-to-understand"
    class="about-ai-entry"
    :class="{ 'has-interactive-paths': interactive }"
  >
    <div v-if="interactive" class="home-ai-paths" @pointerleave="hovered = ''">
      <div v-for="item in entries" :key="item.key" class="home-ai-slot" :class="{ 'is-active': active === item.key }">
      <button type="button" class="home-ai-path" :class="{ 'is-active': active === item.key }" :aria-label="item.accessible" @pointerenter="enter(item.key, $event)" @pointerdown="press(item.key, $event)" @pointerup="release" @pointercancel="release" @focus="focus(item.key, $event)" @blur="focused = ''" @click="openEntry(item.key)">
        <span class="home-ai-label"><i :class="item.icon" aria-hidden="true" /><AiAskTerm v-if="item.key === 'ask'" /><AiViewTerm v-else-if="item.key === 'view'" /><span v-else>Search</span></span>
        <span class="home-ai-copy"><span class="home-ai-copy-full">{{ item.copy }}</span><span class="home-ai-copy-compact">{{ item.compact }}</span></span>
        <span class="home-ai-scene" aria-hidden="true"><HomeArchiveEntryMark :kind="item.key" :active="active === item.key" /></span>
        <ArrowUpRightIcon class="home-ai-arrow" />
      </button>
      </div>
    </div>
    <div v-else class="about-ai-paths">
      <button type="button" class="about-ai-path" @click="openSearchPalette">
        <span class="about-ai-path-label"><i class="fa fa-search" aria-hidden="true"></i> Search</span>
        <strong>Go straight to the source</strong>
        <span class="about-ai-path-copy">Search across the whole site, see the relevant excerpt in context, and open the exact place it came from.</span>
        <span class="about-ai-path-action">Search the site <ArrowUpRightIcon /></span>
      </button>

      <button type="button" class="about-ai-path" @click="openAskPalette">
        <span class="about-ai-path-label"><i class="far fa-comment-dots" aria-hidden="true"></i><AiAskTerm /></span>
        <strong>Ask what you want to know</strong>
        <span class="about-ai-path-copy">Ask a question about Henry and get a direct answer grounded in the site, with citations to the relevant sources.</span>
        <span class="about-ai-path-action">Ask a question <ArrowUpRightIcon /></span>
      </button>

      <button type="button" class="about-ai-path" @click="openViewPalette">
        <span class="about-ai-path-label"><i class="fas fa-cube" aria-hidden="true"></i><AiViewTerm /></span>
        <strong>See Henry through your own lens</strong>
        <span class="about-ai-path-copy">Start with a theme, question, or perspective. AI connects relevant material into a View you can read as an article or visit as an exhibition.</span>
        <span class="about-ai-path-action">Compose a <AiViewTerm /> <ArrowUpRightIcon /></span>
      </button>
    </div>
    <HomeGalleryEntry v-if="interactive" />
  </section>
</template>

<script>
import HomeGalleryEntry from './ui/HomeGalleryEntry.vue';
import AiAskTerm from './ui/AiAskTerm.vue';
import AiViewTerm from './ui/AiViewTerm.vue';
import ArrowUpRightIcon from './ui/ArrowUpRightIcon.vue';
import HomeArchiveEntryMark from './ui/HomeArchiveEntryMark.vue';
import { supportsDesktopHover, watchDesktopHover } from '../utils/desktopHover';

export default {
  name: 'AboutAiEntry',
  components: { HomeGalleryEntry, AiAskTerm, AiViewTerm, ArrowUpRightIcon, HomeArchiveEntryMark },
  props: { interactive: Boolean },
  data: () => ({
    hovered: '', focused: '', pressed: '',
    entries: [
      { key: 'search', icon: 'fa fa-search', copy: 'Find original material and open its exact place on the site.', compact: 'Find original material.', accessible: 'Search the site for original material' },
      { key: 'ask', icon: 'far fa-comment-dots', copy: 'Ask about Henry and get an answer with citations.', compact: 'Answers with citations.', accessible: 'Ask a question about Henry' },
      { key: 'view', icon: 'fas fa-cube', copy: 'Explore your question as an article or an exhibition.', compact: 'Articles and exhibitions.', accessible: 'Compose a focused View' },
    ],
  }),
  computed: { active() { return this.pressed || this.hovered || this.focused; } },
  mounted() { this._stopHover = watchDesktopHover(enabled => { if (!enabled) { this.hovered = ''; this.focused = ''; } }); },
  beforeUnmount() { this._stopHover?.(); window.clearTimeout(this._pressTimer); },
  methods: {
    enter(key, event) { if (event.pointerType !== 'touch' && supportsDesktopHover()) this.hovered = key; },
    focus(key, event) { if (event.target.matches(':focus-visible') && supportsDesktopHover()) this.focused = key; },
    press(key, event) { if (event.pointerType === 'touch') { window.clearTimeout(this._pressTimer); this.pressed = key; } },
    release() { window.clearTimeout(this._pressTimer); this._pressTimer = window.setTimeout(() => { this.pressed = ''; }, 180); },
    openEntry(key) {
      this.hovered = '';
      this.focused = '';
      ({ search: this.openSearchPalette, ask: this.openAskPalette, view: this.openViewPalette })[key]();
    },
    openSearchPalette() {
      window.dispatchEvent(new CustomEvent('open-site-search'));
    },
    openAskPalette() {
      window.dispatchEvent(new CustomEvent('open-site-ask'));
    },
    openViewPalette() {
      window.dispatchEvent(new CustomEvent('open-site-view'));
    },
  },
};
</script>

<style scoped>
/* A stable hit area keeps the three entrances compact at every viewport. */
.home-ai-paths { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.home-ai-slot { min-width: 0; }
.home-ai-path { position: relative; display: flex; flex-direction: column; gap: 12px; box-sizing: border-box; width: 100%; height: 100%; min-height: 128px; padding: 18px 20px; border: 0; border-radius: 2px; overflow: hidden; color: var(--ink, #333); background: var(--surface, #f7f7f5); text-align: left; cursor: pointer; -webkit-appearance: none; appearance: none; -webkit-tap-highlight-color: transparent; transition: background-color 180ms ease; }
.home-ai-label { position: relative; z-index: 1; display: flex; align-items: center; gap: 8px; padding-right: 22px; font: 500 1rem/1.3 'Quattrocento Sans', sans-serif; white-space: nowrap; }
.home-ai-label i, .home-ai-label i:hover { padding: 0; font-size: 14px; color: var(--muted, #777); }
.home-ai-copy { position: relative; z-index: 1; display: block; font: .85rem/1.5 'Quattrocento Sans', sans-serif; color: var(--muted, #777); transition: opacity 160ms ease; }
.home-ai-copy-compact { display: none; }
.home-ai-arrow { position: absolute; top: 21px; right: 20px; width: 13px; height: 13px; opacity: .45; transition: transform 240ms cubic-bezier(.22,1,.36,1), opacity 180ms ease; }
.home-ai-scene { position: absolute; inset: 40px 20px 4px; opacity: 0; transform: translateY(5px); pointer-events: none; transition: opacity 180ms ease, transform 240ms cubic-bezier(.22,1,.36,1); }
.home-ai-path.is-active .home-ai-scene { opacity: 1; transform: translateY(0); }
.home-ai-path.is-active .home-ai-copy { opacity: 0; }
.home-ai-path.is-active .home-ai-arrow { transform: translate(2px,-2px); opacity: 1; }
.home-ai-path:focus-visible { outline: 1px solid currentColor; outline-offset: 4px; }
.home-ai-path:focus-visible .home-ai-copy { opacity: 1; }
.home-ai-path:focus-visible .home-ai-scene { opacity: 0; }
.home-ai-path:focus-visible, .home-ai-path:focus-visible :deep(*) { transition: none !important; }
.home-ai-path:active { background: rgba(var(--site-accent-rgb), .09); }
@media (max-width: 760px) {
  .home-ai-paths { grid-template-columns: 1fr; gap: 8px; }
  .home-ai-path { min-height: 0; padding: 16px 18px; gap: 7px; }
  .home-ai-copy { padding-right: 12px; }
  .home-ai-arrow { top: 19px; right: 18px; }
}
@media (prefers-reduced-motion: reduce) {
  .home-ai-path, .home-ai-path * { transition: none !important; }
}
.about-ai-entry {
  margin: 1.7rem 0 2.35rem;
  padding: clamp(1.15rem, 3vw, 1.55rem);
  background: rgba(var(--site-accent-rgb), 0.035);
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  font-size: 0.85rem;
}
.about-ai-paths {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
}
.about-ai-path {
  display: flex;
  min-width: 0;
  min-height: 15.4rem;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  border: 0;
  border-radius: 0;
  background: rgba(var(--site-accent-rgb), 0.055);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 180ms ease;
}
.about-ai-path-label {
  display: inline-flex;
  align-items: baseline;
  gap: 0.38rem;
  margin-bottom: 0.72rem;
  color: #686868;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.61rem;
  font-weight: 600;
  letter-spacing: 0.055em;
  line-height: 1.2;
  text-transform: uppercase;
}
.about-ai-path-label i { font-size: 0.82em; }
.about-ai-path strong {
  display: block;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.9rem;
  line-height: 1.25;
}
.about-ai-path-copy {
  display: block;
  margin-top: 0.48rem;
  color: #696969;
  font-size: 0.82rem;
  line-height: 1.55;
}
.about-ai-path-action {
  position: relative;
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  margin-top: auto;
  padding-top: 1rem;
  color: #555;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
}
.about-ai-path-action::after {
  content: "";
  position: absolute;
  right: 100%;
  bottom: -0.22rem;
  left: 0;
  height: 1px;
  background: currentColor;
  transition: right 240ms cubic-bezier(0.23, 1, 0.32, 1);
}
.about-ai-path-action > :deep(.site-arrow-up-right) {
  transition: transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
}
.about-ai-path:hover,
.about-ai-path:focus-visible {
  background: rgba(var(--site-accent-rgb), 0.10);
  outline: none;
}
.about-ai-path:hover .about-ai-path-action::after,
.about-ai-path:focus-visible .about-ai-path-action::after { right: 0; }
.about-ai-path:hover .about-ai-path-action > :deep(.site-arrow-up-right),
.about-ai-path:focus-visible .about-ai-path-action > :deep(.site-arrow-up-right) { transform: translate(2px, -2px); }

:global(body.dark-mode .about-ai-entry) { background: rgba(var(--site-accent-rgb), 0.025); }
:global(body.dark-mode .about-ai-path) { background: rgba(var(--site-accent-rgb), 0.045); }
:global(body.dark-mode .about-ai-path:hover),
:global(body.dark-mode .about-ai-path:focus-visible) { background: rgba(var(--site-accent-rgb), 0.085); }
:global(body.dark-mode .about-ai-path-copy) { color: #b6b6b6; }
:global(body.dark-mode .about-ai-path-action) { color: #d0d0d0; }
:global(body.dark-mode .about-ai-path-label) { color: var(--site-text-tertiary); }

/* Keep text and star marks neutral while the surfaces carry a faint paper tint. */
.about-ai-entry :deep(.ai-ask-term-mark),
.about-ai-entry :deep(.ai-view-term-mark) { color: #8a8a8a; }
:global(body.dark-mode .about-ai-entry .ai-ask-term-mark),
:global(body.dark-mode .about-ai-entry .ai-view-term-mark) { color: var(--site-text-tertiary); }

@media (max-width: 640px) {
  .about-ai-entry {
    margin-right: -0.15rem;
    margin-left: -0.15rem;
    padding: 1rem;
  }
  .about-ai-paths { grid-template-columns: 1fr; }
  .about-ai-path { min-height: 0; }
  .about-ai-path-action {
    margin-top: 0.9rem;
    padding-top: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .about-ai-path,
  .about-ai-path-action::after,
  .about-ai-path-action > :deep(.site-arrow-up-right) { transition: none; }
}
</style>
